import React, { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

type SecurityLogRow = {
  id: number;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  ip_address: string;
  created_at: string;
};

const PanelSecurityV1: React.FC = () => {
  const [rows, setRows] = useState<SecurityLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Token manquant');
        const response = await fetch(`${getApiBaseUrl(true)}/panel/v1/security/logs?page=1&limit=25`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!response.ok || !data?.success) throw new Error(data?.error || 'Erreur chargement logs');
        setRows(Array.isArray(data.data) ? data.data : []);
      } catch (e: any) {
        setError(e?.message || 'Erreur');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const severityUi = (severity: SecurityLogRow['severity']) => {
    if (severity === 'critical') return { emoji: '🚨', label: 'Critique', cls: 'border-red-500/40 bg-red-500/15 text-red-200' };
    if (severity === 'high') return { emoji: '⚠️', label: 'Élevée', cls: 'border-orange-500/40 bg-orange-500/15 text-orange-200' };
    if (severity === 'medium') return { emoji: '🟡', label: 'Moyenne', cls: 'border-amber-500/40 bg-amber-500/15 text-amber-200' };
    return { emoji: '🟢', label: 'Faible', cls: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-200' };
  };

  const typeEmoji = (type: string) => {
    const t = String(type || '').toLowerCase();
    if (t.includes('login') || t.includes('auth')) return '🔐';
    if (t.includes('ban') || t.includes('sanction')) return '⛔';
    if (t.includes('rate') || t.includes('limit')) return '⏱️';
    if (t.includes('tamper') || t.includes('cheat')) return '🛡️';
    return '🧾';
  };

  return (
    <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/75 p-4 backdrop-blur-sm">
      <h2 className="mb-4 text-sm font-semibold text-cockpit-text">Logs &amp; traçabilité</h2>
      {loading && <p className="text-cockpit-text-2">Chargement...</p>}
      {error && <p className="text-red-300">{error}</p>}
      {!loading && !error && (
        <div className="overflow-auto">
          <table className="min-w-full text-left text-sm text-cockpit-text">
            <thead>
              <tr>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">ID</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Type</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Message</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Sévérité</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">IP</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-[#1a2537]/80">
                  <td className="px-3 py-2 text-cockpit-text-2">{row.id}</td>
                  <td className="px-3 py-2">
                    <span className="mr-2">{typeEmoji(row.type)}</span>
                    <span className="text-cockpit-text">{row.type}</span>
                  </td>
                  <td className="px-3 py-2 text-cockpit-text">{row.message}</td>
                  <td className="px-3 py-2">
                    {(() => {
                      const ui = severityUi(row.severity);
                      return (
                        <span className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs ${ui.cls}`}>
                          <span>{ui.emoji}</span>
                          <span>{ui.label}</span>
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-cockpit-text-2">{row.ip_address}</td>
                  <td className="px-3 py-2 text-xs text-cockpit-text-2">{new Date(row.created_at).toLocaleString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PanelSecurityV1;
